import React, { useReducer, useEffect, useMemo, useContext } from 'react';
import { UFrameworkReducer, initialState } from './reducers';
import { AUTO_FETCH_EVENTS } from './autoFetchEvents';

export const UFrameworkContext = React.createContext();
export const UFrameworkUpdateContext = React.createContext();

/**
 * Global state provider for a product app.
 *
 * Props:
 *   value      — { sendData, useSocket, SOCKET_ID, ID, ... } injected by the product
 *   autoFetch  — array of keys from AUTO_FETCH_EVENTS this product wants fetched
 *                once a socket session is ready (e.g. ['customFields'])
 *   children   — app tree
 */
export function UFrameworkProvider({ value, autoFetch, children }) {
    const [state, dispatch] = useReducer(UFrameworkReducer, initialState);
    

    const contextValue = useMemo(
        () => ({
            ...state,
            ...(value || {}),
            common: {
                ...(state?.common || {}),
                ...(value?.common || {}),
                fields: state?.common?.fields || value?.common?.fields || {},
            },
        }),
        [state, value],
    );

    return (
        <UFrameworkContext.Provider value={contextValue}>
            <UFrameworkUpdateContext.Provider value={dispatch}>
                <UAutoFetch keys={autoFetch} />
                {children}
            </UFrameworkUpdateContext.Provider>
        </UFrameworkContext.Provider>
    );
}

/**
 * Fires each opted-in event once the socket is connected, and routes the
 * matching responses into the framework reducer. One socket subscription
 * handles every key — no per-event nesting.
 */
function UAutoFetch({ keys }) {
    const dispatch = useContext(UFrameworkUpdateContext);
    const { sendData, useSocket, SOCKET_ID, ID } = useContext(UFrameworkContext);
    const events = (keys || [])
        .map(key => AUTO_FETCH_EVENTS[key])
        .filter(Boolean);

    const { refresh, off } = useSocket(res => {
        if (res?.error) return;
        if (res?.data?.socketId && res?.data?.socketId !== SOCKET_ID) return;
        const match = events.find(cfg => cfg.event === res?.event);
        if (match) dispatch({ type: match.action, payload: res?.data });
    }, 'frameworkAutoFetch');

    useEffect(() => {
        if (!SOCKET_ID || !ID || !events?.length) return;
        refresh();
        events?.forEach(cfg => sendData?.(cfg?.event, cfg?.payload || {}));
        return () => off();
    }, [SOCKET_ID, ID, events?.length]);

    return null;
}
